import React, { use, useMemo } from "react";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { useI18n } from "../../hooks/i18n-hooks";
import ScreenHeader from "./ScreenHeader";
import { BodyMediumText } from "../../components/FormattedText";
import VStack from "../../ui_atoms/VStack";
import { useQuery } from "@apollo/client/react";
import { GET_MISSION } from "../../graphql/core";
import { useApolloErrorSnackbar } from "../../hooks/snackbar";
import Loading from "../../components/Loading";
import { DocumentType, Document } from "../../__generated__/graphql";
import { IconName } from "../../components/Icon";
import { DocumentContext } from "../../contexts/DocumentManager";
import ListGroup from "../../components/ListGroup";

type Props = StackScreenProps<RootStackParamList, "MissionAnnexes">;

const MissionAnnexesScreen: React.FC<Props> = ({ route, navigation }) => {
    const i18n = useI18n();
    const { missionId } = route.params;
    const { data, loading, error } = useQuery(GET_MISSION, {
        variables: { id: missionId },
        skip: !missionId,
    });
    const { showDocument } = use(DocumentContext);
    useApolloErrorSnackbar(error);
    const mission = data?.getMission;
    const documentItems = useMemo(() => {
        const annexes =
            mission?.documents.filter((doc) => doc.type === DocumentType.MissionAnnex) || [];
        return annexes.map((document) => {
            return {
                value: document.id || document.documentUrl || "",
                description: document.title,
                testID: document.id,
                icon: "file" as IconName,
                onPress: () => {
                    showDocument({ document });
                },
            };
        });
    }, [mission, showDocument]);

    return (
        <>
            <ScreenHeader
                useSmallLabel
                onLeftAction={() => navigation.goBack()}
                label={i18n.t("screens.mission_annexes.title")}
            />
            <VStack gap={1} h={0}>
                <VStack h={1}>
                    <BodyMediumText>
                        {i18n.t("screens.mission_annexes.intro", {
                            legalEmployerName: mission?.legalEmployer?.marketingName,
                        })}
                    </BodyMediumText>
                </VStack>

                {loading && <Loading />}
                {!error && !loading && mission && <ListGroup items={documentItems} />}
            </VStack>
        </>
    );
};

export default MissionAnnexesScreen;
